/* cart-gift-selector — Web Component für den Gratisgeschenk-Block
 * im Cart-Drawer.
 *
 * Verantwortungen:
 *   1. Toggle (auf/zu) mit sessionStorage-Persistenz über Cart-Re-Renders
 *   2. Tile-Click: ersetzt vorhandenes Geschenk (change.js → add.js sequentiell)
 *      mit Defensive-Snapshot + UI-Lock
 *   3. Auto-Removal wenn Liquid `data-needs-cleanup="<line_key>"` gesetzt hat
 *      (3-Schicht-Loop-Schutz: Liquid-Konvergenz + In-Flight-Gate + Stale-TTL)
 *   4. Toast nach erfolgreichem Auto-Removal (über sessionStorage gepuffert)
 *   5. Quantity-Fix wenn Liquid `data-needs-quantity-fix="<line_key>"` gesetzt hat
 *      (stiller Reset auf 1, teilt sich das In-Flight-Gate mit Auto-Cleanup,
 *      weil beide Konvergenzen denselben Cart mutieren)
 *
 * Integration mit cart.js: publish theme.pubsub cartUpdate mit source='gift-selector',
 * KEIN Listener auf cart:refresh oder cart:updated (gehören CartDrawer / CartItems).
 *
 * Starterpack-Schutz: Component fasst NUR Lines an, deren key vom Liquid als
 * _gift-Line identifiziert wurde (via data-current-gift-key, data-needs-cleanup
 * oder data-needs-quantity-fix). Vor jedem change.js wird via /cart.js verifiziert,
 * dass die Ziel-Line _gift trägt.
 *
 * Bekannte und akzeptierte Lücken (siehe Plan Schritt 3, bewusst out of scope):
 *   - Lücke 2 (verwaister Toast): SS_AUTO_REMOVED kann liegenbleiben, wenn beim
 *     nächsten Render <gift-selector> nicht im DOM ist (z.B. Cart leer, Block
 *     wird gar nicht gerendert). Folge: harmloser Toast beim nächsten Re-Add.
 *     Akzeptiert, rein kosmetisch.
 *   - Lücke 4 (Multi-Tab-Race): Zwei Tabs können parallel je ein add.js senden
 *     → zwei _gift-Lines im Cart. Liquid warnt im HTML-Kommentar (gift_count > 1),
 *     Selector fasst weiterhin nur die erste an, zweite bleibt liegen.
 *     Akzeptiert: sehr seltener User-Pfad, eine cross-tab BroadcastChannel-
 *     Koordination wäre disproportional zum tatsächlichen Risiko.
 */

const GIFT_PROPERTY_KEY = '_gift';
const GIFT_PROPERTY_VALUE = 'free-7er';
const VISIBLE_GIFT_PROPERTY = 'Gratisgeschenk';
const VISIBLE_GIFT_LABEL = '🎁 Gratis ab 70 €';

const SS_OPEN_KEY = 'sniffys-gift-selector-open';
const SS_CLEANUP_IN_FLIGHT = 'sniffys-gift-cleanup-in-flight';
const SS_AUTO_REMOVED = 'sniffys-gift-auto-removed';
const CLEANUP_STALE_MS = 5000;
const TOAST_VISIBLE_MS = 5000;

if (!customElements.get('gift-selector')) {
  customElements.define('gift-selector', class GiftSelector extends HTMLElement {
    constructor() {
      super();
      this.isSubmitting = false;
    }

    connectedCallback() {
      this.toggleBtn = this.querySelector('[data-toggle]');
      this.body = this.querySelector('[data-body]');
      this.toast = this.querySelector('[data-toast]');
      this.tiles = Array.from(this.querySelectorAll('[data-tile]'));

      this.currentGiftKey = this.dataset.currentGiftKey || '';
      this.currentGiftVariant = this.dataset.currentGiftVariant || '';
      this.needsCleanupKey = this.dataset.needsCleanup || '';
      this.needsQuantityFixKey = this.dataset.needsQuantityFix || '';
      this.state = this.dataset.state || 'locked';

      if (this.toggleBtn) {
        this.toggleBtn.addEventListener('click', this.onToggle.bind(this));
      }
      this.tiles.forEach((tile) => {
        tile.addEventListener('click', this.onTileClick.bind(this));
      });

      this.restoreOpenState();
      this.showPendingToastIfAny();
      this.maybeRunAutoCleanup();
      this.maybeRunQuantityFix();
    }

    /* ─── Toggle ─────────────────────────────────────────────────── */

    onToggle() {
      const expanded = this.toggleBtn.getAttribute('aria-expanded') === 'true';
      this.setOpen(!expanded);
    }

    setOpen(open) {
      this.toggleBtn.setAttribute('aria-expanded', open ? 'true' : 'false');
      if (open) {
        this.body.removeAttribute('hidden');
      } else {
        this.body.setAttribute('hidden', '');
      }
      try {
        sessionStorage.setItem(SS_OPEN_KEY, open ? '1' : '0');
      } catch (e) { /* sessionStorage disabled — open state just won't persist */ }
    }

    restoreOpenState() {
      let stored = null;
      try { stored = sessionStorage.getItem(SS_OPEN_KEY); } catch (e) {}
      if (stored === '1') {
        this.setOpen(true);
      } else if (stored === '0') {
        this.setOpen(false);
      }
      // null = noch nie interagiert → behalte initial-state aus Liquid (open_default)
    }

    /* ─── Toast ──────────────────────────────────────────────────── */

    showPendingToastIfAny() {
      let flag = null;
      try { flag = sessionStorage.getItem(SS_AUTO_REMOVED); } catch (e) {}
      if (!flag) return;
      try { sessionStorage.removeItem(SS_AUTO_REMOVED); } catch (e) {}

      const msg = (window.theme && theme.strings && theme.strings.gift_auto_removed)
        ? theme.strings.gift_auto_removed
        : 'Dein Geschenk wurde entfernt, weil der Mindestbestellwert unterschritten wurde.';
      this.showToast(msg);
    }

    showToast(message) {
      if (!this.toast) return;
      this.toast.textContent = message;
      this.toast.removeAttribute('hidden');
      // Block automatisch öffnen, damit der User den Toast sieht
      this.setOpen(true);
      clearTimeout(this._toastTimer);
      this._toastTimer = setTimeout(() => {
        this.toast.setAttribute('hidden', '');
        this.toast.textContent = '';
      }, TOAST_VISIBLE_MS);
    }

    /* ─── Auto-Cleanup ───────────────────────────────────────────── */

    async maybeRunAutoCleanup() {
      if (!this.needsCleanupKey) return;

      // Schicht 2: In-Flight-Gate — parallele Instanz aktiv?
      let inFlightTs = null;
      try { inFlightTs = sessionStorage.getItem(SS_CLEANUP_IN_FLIGHT); } catch (e) {}
      if (inFlightTs) {
        const age = Date.now() - Number(inFlightTs);
        // Schicht 3: Stale-Threshold — alte Flags verfallen
        if (!Number.isNaN(age) && age < CLEANUP_STALE_MS) {
          return;
        }
      }
      try { sessionStorage.setItem(SS_CLEANUP_IN_FLIGHT, String(Date.now())); } catch (e) {}

      try {
        // Defensive Verifikation: Line wirklich noch da und wirklich _gift?
        const verified = await this.verifyGiftLine(this.needsCleanupKey);
        if (!verified) {
          // Line nicht mehr da oder kein _gift → nichts zu tun
          return;
        }

        await this.removeLine(this.needsCleanupKey);
        try { sessionStorage.setItem(SS_AUTO_REMOVED, '1'); } catch (e) {}
        await this.triggerCartRefresh();
        // Liquid wird beim nächsten Render keinen _gift-Item sehen → kein
        // data-needs-cleanup mehr → Schicht 1 (Liquid-Konvergenz) greift.
      } catch (err) {
        console.warn('[gift-selector] auto-cleanup failed', err);
      } finally {
        try { sessionStorage.removeItem(SS_CLEANUP_IN_FLIGHT); } catch (e) {}
      }
    }

    /* ─── Quantity-Fix ───────────────────────────────────────────── */

    // Stille Korrektur, falls eine _gift-Line mit quantity > 1 im Cart liegt.
    // Primärer Schutz ist das Verstecken des Steppers im Cart-Drawer; dies hier
    // ist nur das Sicherheitsnetz für Alt-Carts oder API-Manipulation.
    // Teilt sich das In-Flight-Gate mit maybeRunAutoCleanup (mutex auf dem Cart).
    async maybeRunQuantityFix() {
      if (!this.needsQuantityFixKey) return;

      let inFlightTs = null;
      try { inFlightTs = sessionStorage.getItem(SS_CLEANUP_IN_FLIGHT); } catch (e) {}
      if (inFlightTs) {
        const age = Date.now() - Number(inFlightTs);
        if (!Number.isNaN(age) && age < CLEANUP_STALE_MS) {
          return;
        }
      }
      try { sessionStorage.setItem(SS_CLEANUP_IN_FLIGHT, String(Date.now())); } catch (e) {}

      try {
        const verified = await this.verifyGiftLine(this.needsQuantityFixKey);
        if (!verified) return;

        await this.setLineQuantity(this.needsQuantityFixKey, 1);
        await this.triggerCartRefresh();
        // Liquid-Konvergenz: bei nächstem Render ist quantity == 1, kein neues Signal.
      } catch (err) {
        console.warn('[gift-selector] quantity-fix failed', err);
      } finally {
        try { sessionStorage.removeItem(SS_CLEANUP_IN_FLIGHT); } catch (e) {}
      }
    }

    /* ─── Tile-Click ─────────────────────────────────────────────── */

    async onTileClick(event) {
      const tile = event.currentTarget;
      if (tile.disabled || tile.getAttribute('aria-disabled') === 'true') return;
      if (this.isSubmitting) return;

      const variantId = tile.dataset.variantId;
      if (!variantId) return;

      // Same tile → no-op (UX-Klarheit: 2. Klick toggelt nicht weg)
      if (variantId === this.currentGiftVariant) return;

      this.isSubmitting = true;
      this.lockTiles(true);

      try {
        // 1. Vorhandenes Geschenk ersetzen?
        if (this.currentGiftKey) {
          const verified = await this.verifyGiftLine(this.currentGiftKey);
          if (verified) {
            await this.removeLine(this.currentGiftKey);
          }
          // else: User hat manuell entfernt — egal, weiter zum Add
        }

        // 2. Neues Geschenk hinzufügen
        await this.addGiftLine(variantId);

        // 3. Cart-Drawer (und ggf. weitere) serverseitig neu rendern lassen
        await this.triggerCartRefresh();
      } catch (err) {
        console.error('[gift-selector] tile click failed', err);
        this.showToast('Geschenk konnte nicht hinzugefügt werden. Bitte erneut versuchen.');
      } finally {
        this.isSubmitting = false;
        // Re-Init nach Server-Re-Render kümmert sich um Re-Enable;
        // falls Re-Render fehlschlägt: tiles wieder freigeben.
        this.lockTiles(false);
      }
    }

    lockTiles(locked) {
      this.tiles.forEach((tile) => {
        if (locked) {
          tile.setAttribute('aria-busy', 'true');
          tile.setAttribute('data-locked', '');
        } else {
          tile.removeAttribute('aria-busy');
          tile.removeAttribute('data-locked');
        }
      });
    }

    /* ─── Cart-API ──────────────────────────────────────────────── */

    async verifyGiftLine(lineKey) {
      try {
        const res = await fetch('/cart.js', { credentials: 'same-origin' });
        const cart = await res.json();
        if (!cart || !Array.isArray(cart.items)) return false;
        const item = cart.items.find((i) => i.key === lineKey);
        if (!item) return false;
        const props = item.properties || {};
        return props[GIFT_PROPERTY_KEY] === GIFT_PROPERTY_VALUE;
      } catch (err) {
        console.warn('[gift-selector] verifyGiftLine failed', err);
        return false;
      }
    }

    async removeLine(lineKey) {
      const res = await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify({ id: lineKey, quantity: 0 }),
      });
      if (!res.ok) {
        throw new Error('change.js failed: ' + res.status);
      }
      return res.json();
    }

    async setLineQuantity(lineKey, quantity) {
      const res = await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify({ id: lineKey, quantity: quantity }),
      });
      if (!res.ok) {
        throw new Error('change.js failed: ' + res.status);
      }
      return res.json();
    }

    collectSectionsToBundle() {
      const sections = [];
      document.documentElement.dispatchEvent(new CustomEvent('cart:bundled-sections', {
        bubbles: true,
        detail: { sections },
      }));
      return sections;
    }

    async addGiftLine(variantId) {
      const sections = this.collectSectionsToBundle();
      const properties = { [GIFT_PROPERTY_KEY]: GIFT_PROPERTY_VALUE };
      properties[VISIBLE_GIFT_PROPERTY] = VISIBLE_GIFT_LABEL;

      const body = JSON.stringify({
        items: [{ id: variantId, quantity: 1, properties }],
        sections,
        sections_url: window.location.pathname,
      });

      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/javascript' },
        credentials: 'same-origin',
        body,
      });
      const parsed = await res.json();
      if (parsed && parsed.status) {
        throw new Error(parsed.description || parsed.message || 'add.js error');
      }
      this._lastAddResponse = parsed;
      return parsed;
    }

    async triggerCartRefresh() {
      // Mimics starterpack-byo.js: fetch fresh cart + publish via theme.pubsub.
      let cartJson = null;
      try {
        const cartUrl = (window.theme && theme.routes && theme.routes.cart_url) || '/cart.js';
        const fetchConfig = (window.theme && theme.utils && theme.utils.fetchConfig)
          ? theme.utils.fetchConfig('json', 'GET')
          : { method: 'GET', headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' } };
        cartJson = await (await fetch(cartUrl, fetchConfig)).json();
        if (cartJson && this._lastAddResponse && this._lastAddResponse.sections) {
          cartJson.sections = this._lastAddResponse.sections;
        }
      } catch (err) {
        console.warn('[gift-selector] cart fetch failed', err);
      }

      try {
        if (window.theme && theme.pubsub && cartJson) {
          theme.pubsub.publish(theme.pubsub.PUB_SUB_EVENTS.cartUpdate, {
            source: 'gift-selector',
            cart: cartJson,
          });
        }
      } catch (err) {
        console.warn('[gift-selector] pubsub publish failed', err);
      }

      try {
        document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
      } catch (err) {}
    }
  });
}
